import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { KpiCard, PageSkeleton, Toggle } from '../ui/primitives';
import { useAdminToast } from './AdminShell';

type Org = {
  id: string; slug: string; display_name: string; plan: string; status: string;
  created_at: string; orders_count: number; operators_count: number;
  members_count: number; has_shopify: boolean; last_synced_at: string | null;
};

const PLANS: [string, string, string[]][] = [
  // Liczby z karty PACKFLOW w katalogu Universe (697) — egzekwuje je baza (migracja 0023).
  ['starter', 'START 349 zł', ['1 stanowisko pakowania', '1 000 paczek / mc (ponad: 0,19 zł)', '3 operatorów (PIN)', '1 urządzenie Android']],
  ['growth', 'PRO 890 zł', ['3 stanowiska pakowania', '5 000 paczek / mc (ponad: 0,19 zł)', '10 operatorów (PIN)', '3 urządzenia Android']],
  ['scale', 'MAX 1 890 zł', ['10 stanowisk pakowania', '20 000 paczek / mc (ponad: 0,19 zł)', 'operatorzy bez limitu', '10 urządzeń Android']],
];

const STATE_PILL: Record<string, [string, string]> = {
  active: ['pill-ok', 'Aktywny'],
  suspended: ['pill-err', 'Zawieszony'],
  trial: ['pill-info', 'Trial'],
};

/* ---- onboarding tenanta ---- */
function OnboardingWizard({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const push = useAdminToast();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [d, setD] = useState({
    name: '', id: '', contact: '', plan: 'starter',
    ucsBaseUrl: '', ucsApiKey: '',
    opFirst: '', opLast: '', pin: '',
  });
  const set = (patch: Partial<typeof d>) => setD((x) => ({ ...x, ...patch }));
  const steps = ['Organizacja', 'Plan', 'Połączenia UCS', 'Operator', 'Podsumowanie'];
  const canNext = step === 0 ? d.name.trim().length > 1 && d.id.trim().length > 2 : true;

  function autoId(name: string) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 20);
  }

  async function create() {
    setBusy(true);
    try {
      const r = await axios.post('/api/super-admin/orgs', { slug: d.id, display_name: d.name, plan: d.plan });
      const orgId = r.data?.id || r.data?.org?.id;
      if (orgId && d.ucsBaseUrl && d.ucsApiKey) {
        try {
          await axios.post(`/api/super-admin/orgs/${orgId}/ucs`, {
            baseUrl: d.ucsBaseUrl, apiKey: d.ucsApiKey,
          });
        } catch (e: any) { push(`UCS: ${e?.response?.data?.error || 'błąd konfiguracji'}`, 'err'); }
      }
      if (orgId && d.opFirst && d.pin) {
        try {
          await axios.post(`/api/super-admin/orgs/${orgId}/operators`, {
            first_name: d.opFirst, last_name: d.opLast || '—', pin: d.pin, role: 'operator',
          });
        } catch (e: any) { push(`Operator: ${e?.response?.data?.error || 'błąd tworzenia'}`, 'err'); }
      }
      push(`Tenant „${d.name}” utworzony · onboarding zakończony`);
      onDone();
      onClose();
    } catch (e: any) {
      push(e?.response?.data?.error || 'Błąd tworzenia tenanta', 'err');
    } finally { setBusy(false); }
  }

  return (
    <div className="wiz-scrim" onClick={onClose}>
      <div className="wiz" onClick={(e) => e.stopPropagation()}>
        <div className="wiz-head">
          <div style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'grid', placeItems: 'center' }}><Icon name="building-2" size={18} /></div>
          <div className="wt">Onboarding tenanta</div>
          <button className="btn btn-icon btn-ghost right" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="wiz-steps">
          {steps.map((s, i) => (
            <div key={s} className={`wiz-pip ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <span className="n">{i < step ? <Icon name="check" size={13} /> : i + 1}</span>{s}
            </div>
          ))}
        </div>
        <div className="wiz-body">
          {step === 0 && (
            <div>
              <h4>Dane organizacji</h4><div className="wsub">Podstawowe informacje o nowym tenancie.</div>
              <div className="wfield"><label>Nazwa organizacji</label><input value={d.name} onChange={(e) => set({ name: e.target.value, id: autoId(e.target.value) })} placeholder="np. Piekarnia Złoty Kłos" autoFocus /></div>
              <div className="wfield-row">
                <div className="wfield"><label>Identyfikator (slug / subdomena)</label><input value={d.id} onChange={(e) => set({ id: e.target.value })} placeholder="zloty-klos" /></div>
                <div className="wfield"><label>E-mail kontaktowy</label><input value={d.contact} onChange={(e) => set({ contact: e.target.value })} placeholder="admin@firma.pl" /></div>
              </div>
            </div>
          )}
          {step === 1 && (
            <div>
              <h4>Plan</h4><div className="wsub">Możesz zmienić w dowolnym momencie.</div>
              <div className="plan-grid">
                {PLANS.map(([id, label, feats]) => (
                  <div key={id} className={`plan-card ${d.plan === id ? 'sel' : ''}`} onClick={() => set({ plan: id })}>
                    <div className="pn">{label}</div><div className="pp">plan {id}</div>
                    <ul>{feats.map((f) => <li key={f}>{f}</li>)}</ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h4>Połączenia · Universal Carrier Service</h4>
              <div className="wsub">Kurierzy i Shopify są skonfigurowani centralnie w UCS — PackFlow potrzebuje tylko adresu UCS i klucza API tenanta (jeden klucz = jeden tenant UCS).</div>
              <div className="wfield"><label>Adres UCS (baseUrl)</label><input value={d.ucsBaseUrl} onChange={(e) => set({ ucsBaseUrl: e.target.value })} placeholder="https://ucs.insolitum.ai" /></div>
              <div className="wfield"><label>Klucz API UCS</label><input type="password" value={d.ucsApiKey} onChange={(e) => set({ ucsApiKey: e.target.value })} placeholder="ucs_live_…" /></div>
            </div>
          )}
          {step === 3 && (
            <div>
              <h4>Pierwszy operator</h4><div className="wsub">Operator loguje się PIN-em na stacji roboczej.</div>
              <div className="wfield-row">
                <div className="wfield"><label>Imię operatora</label><input value={d.opFirst} onChange={(e) => set({ opFirst: e.target.value })} placeholder="np. Anna" /></div>
                <div className="wfield"><label>Nazwisko</label><input value={d.opLast} onChange={(e) => set({ opLast: e.target.value })} placeholder="Kowalska" /></div>
              </div>
              <div className="wfield" style={{ maxWidth: 220 }}><label>PIN (4-8 cyfr)</label><input value={d.pin} maxLength={8} onChange={(e) => set({ pin: e.target.value.replace(/\D/g, '') })} placeholder="••••" /></div>
            </div>
          )}
          {step === 4 && (
            <div>
              <h4>Podsumowanie</h4><div className="wsub">Sprawdź dane przed utworzeniem.</div>
              <dl style={{ margin: 0 }}>
                <div className="summary-row"><dt>Organizacja</dt><dd>{d.name || '—'} <span className="muted mono">({d.id || '—'})</span></dd></div>
                <div className="summary-row"><dt>Plan</dt><dd>{d.plan}</dd></div>
                <div className="summary-row"><dt>UCS</dt><dd className="mono">{d.ucsBaseUrl || '—'}{d.ucsApiKey ? ' · klucz ••••' : ''}</dd></div>
                <div className="summary-row"><dt>Operator</dt><dd>{d.opFirst ? `${d.opFirst} ${d.opLast}` : '—'} {d.pin && <span className="mono">· PIN ••••</span>}</dd></div>
              </dl>
            </div>
          )}
        </div>
        <div className="wiz-foot">
          {step > 0 && <button className="btn btn-ghost" onClick={() => setStep((s) => s - 1)}><Icon name="arrow-left" size={16} /> Wstecz</button>}
          <div className="right muted" style={{ fontSize: 13 }}>Krok {step + 1} z {steps.length}</div>
          {step < steps.length - 1
            ? <button className="btn btn-primary" disabled={!canNext} onClick={() => setStep((s) => s + 1)}>Dalej <Icon name="arrow-right" size={16} /></button>
            : <button className="btn btn-primary" disabled={busy} onClick={create}><Icon name="check" size={16} /> {busy ? 'Tworzenie…' : 'Utwórz tenanta'}</button>}
        </div>
      </div>
    </div>
  );
}

/* ---- konfiguracja tenanta (drawer) ---- */
function TenantDrawer({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const push = useAdminToast();
  const qc = useQueryClient();
  const { data, refetch } = useQuery<any>({
    queryKey: ['tenant', orgId],
    queryFn: async () => (await axios.get(`/api/super-admin/orgs/${orgId}`)).data,
  });
  const [tab, setTab] = useState('ucs');
  const [ucsForm, setUcsForm] = useState({ baseUrl: '', apiKey: '' });
  const [op, setOp] = useState({ first_name: '', last_name: '', pin: '', role: 'operator' });
  const [printer, setPrinter] = useState({ name: '', ip: '', port: 9100, protocol: 'EPL', format: '80x50mm', is_default: false });
  const [member, setMember] = useState({ user_id: '', role: 'admin' });
  const [numery, setNumery] = useState<Record<string, string>>({});
  const { data: limity } = useQuery<any>({
    queryKey: ['tenant-limity', orgId],
    queryFn: async () => (await axios.get(`/api/super-admin/orgs/${orgId}/limity`)).data,
    enabled: tab === 'limity',
  });
  const { data: rozliczenia, refetch: refetchRozliczenia } = useQuery<any>({
    queryKey: ['tenant-rozliczenia', orgId],
    queryFn: async () => (await axios.get(`/api/super-admin/rozliczenia`, { params: { org: orgId } })).data,
    enabled: tab === 'limity',
  });

  const org = data?.org;

  async function zafakturuj(okres: string) {
    try {
      await axios.post(`/api/super-admin/rozliczenia/${orgId}/${okres.slice(0, 7)}/faktura`, { numer_faktury: numery[okres] });
      push('Nadwyżka oznaczona jako zafakturowana'); refetchRozliczenia();
    } catch (e: any) { push(e?.response?.data?.error?.message || 'Błąd zapisu faktury', 'err'); }
  }

  async function saveUcs() {
    try {
      await axios.post(`/api/super-admin/orgs/${orgId}/ucs`, ucsForm);
      setUcsForm({ baseUrl: ucsForm.baseUrl, apiKey: '' });
      push('Konfiguracja UCS zapisana'); refetch(); qc.invalidateQueries({ queryKey: ['tenants'] });
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zapisu UCS', 'err'); }
  }
  async function addOperator() {
    try {
      await axios.post(`/api/super-admin/orgs/${orgId}/operators`, op);
      setOp({ first_name: '', last_name: '', pin: '', role: 'operator' });
      push('Operator dodany'); refetch();
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd dodawania operatora', 'err'); }
  }
  async function addPrinter() {
    try {
      await axios.post(`/api/super-admin/orgs/${orgId}/printers`, printer);
      setPrinter({ name: '', ip: '', port: 9100, protocol: 'EPL', format: '80x50mm', is_default: false });
      push('Drukarka dodana'); refetch();
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd dodawania drukarki', 'err'); }
  }
  async function addMember() {
    try {
      await axios.post(`/api/super-admin/orgs/${orgId}/members`, member);
      setMember({ user_id: '', role: 'admin' });
      push('Admin dopięty do organizacji'); refetch();
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd dodawania admina', 'err'); }
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer" style={{ width: 640 }}>
        <div className="drawer-head">
          <button className="btn btn-icon btn-ghost" onClick={onClose}><Icon name="x" size={18} /></button>
          <h2 style={{ fontFamily: 'var(--font-display)' }}>{org?.display_name || '…'}</h2>
          {org && <span className="tag-attr">{org.slug}</span>}
          {org && <span className={`pill ${STATE_PILL[org.status]?.[0] || 'pill-neutral'}`}>{STATE_PILL[org.status]?.[1] || org.status}</span>}
        </div>
        <div className="drawer-body">
          <div className="tabs">
            {([['ucs', 'UCS', 'orbit'], ['operators', 'Operatorzy', 'users'], ['printers', 'Drukarki', 'printer'], ['members', 'Adminowie', 'shield'], ['limity', 'Limity i nadwyżki', 'gauge']] as const).map(([id, lbl, ic]) => (
              <button key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}><Icon name={ic} size={15} /> {lbl}</button>
            ))}
          </div>

          {tab === 'ucs' && (
            <div>
              <div className="pill pill-info" style={{ height: 'auto', padding: '8px 12px', borderRadius: 10, marginBottom: 14, width: '100%' }}>
                <Icon name="info" size={13} /> UCS jest jedyną integracją PackFlow v2 — zamówienia, etykiety, fulfillment i tracking. Klucz jest szyfrowany w app_settings tenanta.
              </div>
              <div className="wfield"><label>Adres UCS (baseUrl)</label><input value={ucsForm.baseUrl} onChange={(e) => setUcsForm({ ...ucsForm, baseUrl: e.target.value })} placeholder="https://ucs.insolitum.ai" /></div>
              <div className="wfield"><label>Klucz API (ucs_live_… / ucs_test_…)</label><input type="password" value={ucsForm.apiKey} onChange={(e) => setUcsForm({ ...ucsForm, apiKey: e.target.value })} /></div>
              <button className="btn btn-primary" disabled={!ucsForm.baseUrl || !ucsForm.apiKey} onClick={saveUcs}><Icon name="save" size={16} /> Zapisz</button>
            </div>
          )}

          {tab === 'operators' && (
            <div>
              <div className="tablewrap"><table className="tbl">
                <thead><tr><th>Imię</th><th>Nazwisko</th><th>Rola</th><th>Aktywny</th></tr></thead>
                <tbody>{(data?.operators || []).map((o: any) => (
                  <tr key={o.id}><td>{o.first_name}</td><td>{o.last_name}</td><td><span className="pill pill-neutral">{o.role}</span></td><td>{o.is_active ? 'tak' : 'nie'}</td></tr>
                ))}</tbody>
              </table></div>
              <div className="sb-sec" style={{ padding: '18px 0 8px' }}>Nowy operator</div>
              <div className="wfield-row">
                <div className="wfield"><label>Imię</label><input value={op.first_name} onChange={(e) => setOp({ ...op, first_name: e.target.value })} /></div>
                <div className="wfield"><label>Nazwisko</label><input value={op.last_name} onChange={(e) => setOp({ ...op, last_name: e.target.value })} /></div>
              </div>
              <div className="wfield-row">
                <div className="wfield"><label>PIN (4-8 cyfr)</label><input value={op.pin} onChange={(e) => setOp({ ...op, pin: e.target.value.replace(/\D/g, '') })} /></div>
                <div className="wfield"><label>Rola</label>
                  <select value={op.role} onChange={(e) => setOp({ ...op, role: e.target.value })}>
                    <option value="operator">operator</option><option value="lead">lead</option><option value="trainee">trainee</option>
                  </select>
                </div>
              </div>
              <button className="btn btn-primary" disabled={!op.first_name || !op.last_name || !op.pin} onClick={addOperator}><Icon name="plus" size={16} /> Dodaj</button>
            </div>
          )}

          {tab === 'printers' && (
            <div>
              <div className="tablewrap"><table className="tbl">
                <thead><tr><th>Nazwa</th><th>IP</th><th>Port</th><th>Protokół</th><th>Format</th><th>Domyślna</th></tr></thead>
                <tbody>{(data?.printers || []).map((p: any) => (
                  <tr key={p.id}><td style={{ fontWeight: 600 }}>{p.name}</td><td className="mono">{p.ip}</td><td className="num">{p.port}</td><td><span className="tag-attr">{p.protocol}</span></td><td>{p.format}</td><td>{p.is_default ? <span className="pill pill-info">Domyślna</span> : ''}</td></tr>
                ))}</tbody>
              </table></div>
              <div className="sb-sec" style={{ padding: '18px 0 8px' }}>Nowa drukarka</div>
              <div className="wfield-row">
                <div className="wfield"><label>Nazwa</label><input value={printer.name} onChange={(e) => setPrinter({ ...printer, name: e.target.value })} /></div>
                <div className="wfield"><label>IP</label><input value={printer.ip} onChange={(e) => setPrinter({ ...printer, ip: e.target.value })} /></div>
              </div>
              <div className="wfield-row">
                <div className="wfield"><label>Port</label><input type="number" value={printer.port} onChange={(e) => setPrinter({ ...printer, port: +e.target.value })} /></div>
                <div className="wfield"><label>Protokół</label><input value={printer.protocol} onChange={(e) => setPrinter({ ...printer, protocol: e.target.value })} /></div>
              </div>
              <button className="btn btn-primary" disabled={!printer.name || !printer.ip} onClick={addPrinter}><Icon name="plus" size={16} /> Dodaj</button>
            </div>
          )}

          {tab === 'limity' && (
            <div>
              <div className="pill pill-info" style={{ height: 'auto', padding: '8px 12px', borderRadius: 10, marginBottom: 14, width: '100%' }}>
                <Icon name="info" size={13} /> Stanowiska, urządzenia i operatorzy ponad limit planu są odrzucane przy parowaniu i nadaniu PIN-u. Paczki ponad limit przechodzą i trafiają na fakturę nadwyżek (0,19 zł netto).
              </div>
              <div className="tablewrap"><table className="tbl">
                <thead><tr><th>Limit</th><th>W użyciu</th><th>Plan {limity?.plan}</th></tr></thead>
                <tbody>{(['stanowiska_pakowania', 'paczki_mc', 'operatorzy', 'urzadzenia_android'] as const).map((k) => (
                  <tr key={k}><td>{k}</td><td className="num">{limity?.uzycie?.[k] ?? '—'}</td><td className="num">{limity?.limity?.[k] === -1 ? 'bez limitu' : (limity?.limity?.[k] ?? '—')}</td></tr>
                ))}</tbody>
              </table></div>
              <div className="sb-sec" style={{ padding: '18px 0 8px' }}>Zamknięte miesiące (paczki)</div>
              <div className="tablewrap"><table className="tbl">
                <thead><tr><th>Miesiąc</th><th>Paczki</th><th>Limit</th><th>Nadwyżka</th><th>Netto</th><th>Faktura</th></tr></thead>
                <tbody>{(rozliczenia?.rozliczenia || []).map((r: any) => (
                  <tr key={r.okres}>
                    <td className="mono">{String(r.okres).slice(0, 7)}</td><td className="num">{r.ilosc}</td>
                    <td className="num">{r.limit_planu ?? 'bez limitu'}</td><td className="num">{r.nadwyzka}</td>
                    <td className="num">{Number(r.kwota_netto).toFixed(2)} {r.waluta}</td>
                    <td>{r.status === 'do_zafakturowania' ? (
                      <span style={{ display: 'flex', gap: 6 }}>
                        <input style={{ width: 120 }} placeholder="nr faktury" value={numery[r.okres] || ''} onChange={(e) => setNumery({ ...numery, [r.okres]: e.target.value })} />
                        <button className="btn btn-primary" disabled={!numery[r.okres]} onClick={() => zafakturuj(r.okres)}>Zapisz</button>
                      </span>
                    ) : r.status === 'zafakturowano' ? <span className="pill pill-info">{r.numer_faktury}</span> : <span className="muted">bez nadwyżki</span>}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            </div>
          )}

          {tab === 'members' && (
            <div>
              <div className="pill pill-info" style={{ height: 'auto', padding: '8px 12px', borderRadius: 10, marginBottom: 14, width: '100%' }}>
                <Icon name="info" size={13} /> Admin musi mieć konto w Supabase Auth — tutaj dopinasz istniejące user_id.
              </div>
              <div className="tablewrap"><table className="tbl">
                <thead><tr><th>user_id</th><th>Rola</th><th>Dodano</th></tr></thead>
                <tbody>{(data?.members || []).map((m: any) => (
                  <tr key={m.user_id}><td><code className="mono" style={{ fontSize: 11 }}>{m.user_id}</code></td><td><span className="pill pill-neutral">{m.role}</span></td><td className="mono muted" style={{ fontSize: 12 }}>{new Date(m.created_at).toLocaleString('pl-PL')}</td></tr>
                ))}</tbody>
              </table></div>
              <div className="sb-sec" style={{ padding: '18px 0 8px' }}>Dopnij admina</div>
              <div className="wfield"><label>user_id (Supabase)</label><input value={member.user_id} onChange={(e) => setMember({ ...member, user_id: e.target.value })} /></div>
              <button className="btn btn-primary" disabled={!member.user_id} onClick={addMember}><Icon name="plus" size={16} /> Dodaj</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function SuperAdminPage() {
  const push = useAdminToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState('tenants');
  const [wizard, setWizard] = useState(false);
  const [openTenant, setOpenTenant] = useState<string | null>(null);

  const { data: metrics, isLoading } = useQuery<any>({
    queryKey: ['sa-metrics'],
    queryFn: async () => (await axios.get('/api/super-admin/metrics')).data,
    refetchInterval: 30000,
  });
  const { data: tenants = [], refetch: refetchTenants } = useQuery<Org[]>({
    queryKey: ['tenants'],
    queryFn: async () => (await axios.get('/api/super-admin/orgs')).data,
  });
  const { data: audit = [] } = useQuery<any[]>({
    queryKey: ['sa-audit'],
    queryFn: async () => (await axios.get('/api/super-admin/audit', { params: { limit: 200 } })).data,
    enabled: tab === 'audit',
  });
  const { data: printErrors = [] } = useQuery<any[]>({
    queryKey: ['sa-print-errors'],
    queryFn: async () => (await axios.get('/api/super-admin/print-jobs/errors')).data,
    refetchInterval: 30000,
  });

  async function patchOrg(id: string, patch: { status?: string }) {
    try {
      await axios.patch(`/api/super-admin/orgs/${id}`, patch);
      refetchTenants();
      qc.invalidateQueries({ queryKey: ['sa-metrics'] });
      push(patch.status === 'suspended' ? 'Tenant zawieszony' : 'Tenant aktywowany');
    } catch (e: any) { push(e?.response?.data?.error || 'Błąd zmiany statusu', 'err'); }
  }

  if (isLoading && !metrics) return <PageSkeleton />;

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Super-admin</h1><div className="ph-sub">Obszar ponad-tenantowy · cała platforma PackFlow</div></div>
        <div className="actions"><button className="btn btn-primary" onClick={() => setWizard(true)}><Icon name="plus" size={16} /> Nowy tenant</button></div>
      </div>
      <div className="kpi-row" style={{ marginBottom: 16 }}>
        <KpiCard icon="building-2" label="Tenanci" value={metrics?.orgs_total ?? tenants.length} delta={metrics?.orgs_suspended ? `${metrics.orgs_suspended} zawieszonych` : undefined} dir="up" />
        <KpiCard icon="package" label="Zamówienia / 24h" value={metrics?.orders_24h ?? '—'} />
        <KpiCard icon="users" label="Operatorzy" value={metrics?.operators_total ?? '—'} />
        <KpiCard icon="alert-triangle" label="Błędy druku 24h" value={metrics?.print_jobs_errors_24h ?? printErrors.length} />
      </div>
      <div className="tabs">
        {([['tenants', 'Tenanci', 'building-2'], ['audit', 'Audyt', 'scroll-text'], ['errors', 'Błędy druku', 'printer']] as const).map(([id, lbl, ic]) => (
          <button key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
            <Icon name={ic} size={15} /> {lbl}{id === 'errors' && printErrors.length > 0 && <span className="cnt">{printErrors.length}</span>}
          </button>
        ))}
      </div>

      {tab === 'tenants' && (
        <div className="card"><div className="tablewrap"><table className="tbl">
          <thead><tr><th>Organizacja</th><th>Plan</th><th>Status</th><th>Zamówienia</th><th>Operatorzy</th><th>Shopify</th><th></th></tr></thead>
          <tbody>{tenants.map((t) => {
            const [cls, lbl] = STATE_PILL[t.status] || ['pill-neutral', t.status];
            return (
              <tr key={t.id}>
                <td><b>{t.display_name}</b><div className="muted mono" style={{ fontSize: 12 }}>{t.slug}</div></td>
                <td><span className="tag-attr">{t.plan}</span></td>
                <td><span className={`pill ${cls}`}>{lbl}</span></td>
                <td className="num">{t.orders_count}</td>
                <td className="num">{t.operators_count}</td>
                <td>{t.has_shopify ? <span className="pill pill-ok"><Icon name="check" size={12} /> OK</span> : <span className="muted">—</span>}</td>
                <td className="right"><div className="flex" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setOpenTenant(t.id)}><Icon name="settings" size={14} /></button>
                  {t.status === 'suspended'
                    ? <button className="btn btn-ghost btn-sm" onClick={() => patchOrg(t.id, { status: 'active' })}>Aktywuj</button>
                    : <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-text)' }} onClick={() => patchOrg(t.id, { status: 'suspended' })}>Zawieś</button>}
                </div></td>
              </tr>
            );
          })}</tbody>
        </table></div>
          {tenants.length === 0 && <div className="empty"><div className="ei"><Icon name="building-2" size={28} /></div><h3>Brak tenantów</h3><p>Dodaj pierwszą organizację do platformy.</p></div>}
        </div>
      )}

      {tab === 'audit' && (
        <div className="card"><div className="tablewrap"><table className="tbl">
          <thead><tr><th>Czas</th><th>Użytkownik</th><th>Akcja</th><th>Obiekt</th><th>Org</th></tr></thead>
          <tbody>{audit.map((a: any) => (
            <tr key={a.id}>
              <td className="mono num" style={{ fontSize: 13 }}>{new Date(a.created_at).toLocaleString('pl-PL')}</td>
              <td>{a.actor_email || a.actor_kind}</td>
              <td><span className="mono tag-attr">{a.action}</span></td>
              <td className="muted" style={{ fontSize: 13 }}>{a.target_type}{a.target_id ? `:${a.target_id}` : ''}</td>
              <td className="mono muted" style={{ fontSize: 11 }}>{a.org_id ? a.org_id.slice(0, 8) : '—'}</td>
            </tr>
          ))}</tbody>
        </table></div>
          {audit.length === 0 && <div className="empty"><div className="ei"><Icon name="scroll-text" size={28} /></div><h3>Brak wpisów audytu</h3></div>}
        </div>
      )}

      {tab === 'errors' && (
        <div className="card">
          {printErrors.length === 0 ? (
            <div className="empty"><div className="ei"><Icon name="check-circle-2" size={28} /></div><h3>Brak błędów druku</h3></div>
          ) : (
            <div className="tablewrap"><table className="tbl">
              <thead><tr><th>Czas</th><th>Org</th><th>Dokument</th><th>Drukarka</th><th>Błąd</th></tr></thead>
              <tbody>{printErrors.map((e: any) => (
                <tr key={e.id}>
                  <td className="mono num" style={{ fontSize: 13 }}>{new Date(e.created_at).toLocaleString('pl-PL')}</td>
                  <td className="mono muted" style={{ fontSize: 11 }}>{e.org_id ? String(e.org_id).slice(0, 8) : '—'}</td>
                  <td className="mono" style={{ fontSize: 13 }}>{e.document_type}</td>
                  <td className="mono">{e.printer_ip}</td>
                  <td><span className="pill pill-err"><Icon name="alert-circle" size={12} /> {e.error}</span></td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      )}

      {wizard && <OnboardingWizard onClose={() => setWizard(false)} onDone={() => refetchTenants()} />}
      {openTenant && <TenantDrawer orgId={openTenant} onClose={() => setOpenTenant(null)} />}
    </div>
  );
}
